import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

export interface Step08FormValues {
    memoryShortTermProblem: boolean;
    memoryLongTermProblem: boolean;
    orientedToPerson: boolean;
    orientedToPlace: boolean;
    orientedToTime: boolean;
    cognitiveDecisionMaking: "independent" | "modifiedIndependence" | "moderatelyImpaired" | "severelyImpaired" | "";
    cognitiveComments: string;
}

export const DEFAULT_STEP08: Step08FormValues = {
    memoryShortTermProblem: false,
    memoryLongTermProblem: false,
    orientedToPerson: false,
    orientedToPlace: false,
    orientedToTime: false,
    cognitiveDecisionMaking: "",
    cognitiveComments: "",
};

export interface Step08Props {
    sectionData: Partial<Step08FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step08FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const DECISION_LEVELS: Array<{ value: Step08FormValues["cognitiveDecisionMaking"]; label: string }> = [
    { value: "independent", label: "Decisions consistent, reasonable, and organized — reflecting lifestyle, culture, values. (Independent)" },
    { value: "modifiedIndependence", label: "Organized daily routine, safe decisions in familiar situations; some difficulty in new situations. (Modified Independence)" },
    { value: "moderatelyImpaired", label: "Decisions are poor; requires reminders, cues, and supervision in planning/organizing. (Moderately Impaired)" },
    { value: "severelyImpaired", label: "Decision-making severely impaired; never/rarely makes decisions. (Severely Impaired)" },
];

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const Step08CognitiveAssessment: React.FC<Step08Props> = ({
    sectionData, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const defaultValues: Step08FormValues = { ...DEFAULT_STEP08, ...(sectionData ?? {}) };
    const { control, handleSubmit, watch, reset } = useForm<Step08FormValues>({ defaultValues });

    const onSubmit = async (data: Step08FormValues) => { await onSave(data); };
    const handleSave = async (data: Step08FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Cognitive assessment saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    const handleSaveAndContinue = async (data: Step08FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Cognitive Assessment", "Memory, orientation, and decision-making summary.", "pi pi-chart-bar")}
                            <div className="p-3 grid">
                                <InfoRow label="Short-Term Memory Problem" value={values.memoryShortTermProblem ? "Yes" : "No"} />
                                <InfoRow label="Long-Term Memory Problem" value={values.memoryLongTermProblem ? "Yes" : "No"} />
                                <InfoRow label="Oriented to Person" value={values.orientedToPerson ? "Yes" : "No"} />
                                <InfoRow label="Oriented to Place" value={values.orientedToPlace ? "Yes" : "No"} />
                                <InfoRow label="Oriented to Time" value={values.orientedToTime ? "Yes" : "No"} />
                                <InfoRow label="Decision Making" value={values.cognitiveDecisionMaking || undefined} />
                                <InfoRow label="Comments" value={values.cognitiveComments} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Short-Term Memory */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Short-Term Memory", "Assessment of recent memory function.", "pi pi-clock")}
                            <div className="p-3">
                                <p className="text-sm text-color-secondary mb-2">
                                    <strong>Method 1:</strong> Ask the individual to describe a recent event you both had opportunity to remember (breakfast, recent meal, weather).
                                </p>
                                <p className="text-sm text-color-secondary mb-3">
                                    <strong>Method 2:</strong> Say three unrelated objects slowly (e.g. table, comb, tree). After 5 minutes, ask them to recall. Inability to recall all three indicates memory problems.
                                </p>
                                <Controller name="memoryShortTermProblem" control={control} render={({ field: f }) => (
                                    <div className="flex align-items-center gap-2">
                                        <Checkbox inputId="shortTermProblem" checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                                        <label htmlFor="shortTermProblem" className="text-sm cursor-pointer font-medium">Short-term memory problem identified</label>
                                    </div>
                                )} />
                            </div>
                        </Card>

                        {/* Long-Term Memory & Orientation */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Long-Term Memory and Orientation", "Assessment of long-term memory and orientation.", "pi pi-history")}
                            <div className="p-3">
                                <p className="text-sm text-color-secondary mb-3">
                                    Ask the individual several of the following: What is your name? What day is it? Where do you live? Are you married? What is your spouse's name? Do you have children? When is your birthday? What year were you born? Verify answers for accuracy.
                                </p>
                                <Controller name="memoryLongTermProblem" control={control} render={({ field: f }) => (
                                    <div className="flex align-items-center gap-2 mb-3">
                                        <Checkbox inputId="longTermProblem" checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                                        <label htmlFor="longTermProblem" className="text-sm cursor-pointer font-medium">Long-term memory problem identified</label>
                                    </div>
                                )} />
                                <p className="font-semibold text-sm mb-2">Orientation</p>
                                <div className="flex flex-wrap gap-4">
                                    <Controller name="orientedToPerson" control={control} render={({ field: f }) => (
                                        <div className="flex align-items-center gap-2">
                                            <Checkbox inputId="orientedToPerson" checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                                            <label htmlFor="orientedToPerson" className="text-sm cursor-pointer">Oriented to Person</label>
                                        </div>
                                    )} />
                                    <Controller name="orientedToPlace" control={control} render={({ field: f }) => (
                                        <div className="flex align-items-center gap-2">
                                            <Checkbox inputId="orientedToPlace" checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                                            <label htmlFor="orientedToPlace" className="text-sm cursor-pointer">Oriented to Place</label>
                                        </div>
                                    )} />
                                    <Controller name="orientedToTime" control={control} render={({ field: f }) => (
                                        <div className="flex align-items-center gap-2">
                                            <Checkbox inputId="orientedToTime" checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                                            <label htmlFor="orientedToTime" className="text-sm cursor-pointer">Oriented to Time</label>
                                        </div>
                                    )} />
                                </div>
                            </div>
                        </Card>

                        {/* Decision-Making */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Decision-Making", "Determine how the individual makes decisions about everyday tasks.", "pi pi-sitemap")}
                            <div className="p-3">
                                <p className="text-sm text-color-secondary mb-3">
                                    Consult with caregivers, family, and others who know this individual to understand how they are presently functioning.
                                </p>
                                <Controller name="cognitiveDecisionMaking" control={control} render={({ field: f }) => (
                                    <div className="flex flex-column gap-2">
                                        {DECISION_LEVELS.map(opt => (
                                            <div key={opt.value} className="flex align-items-start gap-2">
                                                <RadioButton
                                                    inputId={`dm_${opt.value}`}
                                                    value={opt.value}
                                                    checked={f.value === opt.value}
                                                    onChange={() => f.onChange(opt.value)}
                                                    style={{ marginTop: 2 }}
                                                />
                                                <label htmlFor={`dm_${opt.value}`} className="text-sm cursor-pointer">{opt.label}</label>
                                            </div>
                                        ))}
                                    </div>
                                )} />
                            </div>
                        </Card>

                        {/* Comments */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Comments", "Additional notes on cognitive assessment.", "pi pi-comment")}
                            <div className="p-3">
                                <Controller name="cognitiveComments" control={control} render={({ field: f }) => (
                                    <InputTextarea {...f} className="w-full" rows={4} autoResize placeholder="Comments on cognitive assessment" />
                                )} />
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step08CognitiveAssessment;
